import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <section className="pt-32 pb-16 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white border-2 border-coral-orange/30 rounded-2xl p-8 shadow-md">
              <h1 className="font-display text-2xl text-text-dark mb-3">Invalid Reset Link</h1>
              <p className="font-body text-text-medium mb-6">
                This password reset link is invalid or has been used. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-6 py-3 bg-gradient-to-r from-mint-green to-sky-blue text-white font-display rounded-xl"
                data-testid="request-new-link"
              >
                Request New Link
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-mint-green hover:text-mint-green/80 font-body text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          <div className="bg-white border-2 border-soft-gray/50 rounded-2xl p-8 shadow-md">
            {success ? (
              <div className="text-center" data-testid="reset-success">
                <div className="w-16 h-16 bg-mint-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-mint-green" />
                </div>
                <h1 className="font-display text-2xl text-text-dark mb-3">Password Reset!</h1>
                <p className="font-body text-text-medium mb-6">
                  Your password has been updated. You can now log in with your new password.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green text-white font-display text-lg rounded-xl transition-all shadow-md"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-lavender/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-lavender" />
                  </div>
                  <h1 className="font-display text-2xl text-text-dark mb-2">Set new password</h1>
                  <p className="font-body text-sm text-text-medium">
                    Choose a strong password for your TypeTown account.
                  </p>
                </div>

                {error && (
                  <div className="bg-coral-orange/10 border border-coral-orange/30 rounded-lg p-3 mb-4 text-coral-orange font-body text-sm text-center" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                  <div>
                    <label className="block font-body font-semibold text-sm text-text-dark mb-2">New password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 bg-cream border-2 border-soft-gray/50 rounded-xl text-text-dark font-body focus:outline-none focus:border-lavender focus:ring-2 focus:ring-lavender/20 pr-12"
                        placeholder="At least 8 characters"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-medium/50 hover:text-text-medium"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block font-body font-semibold text-sm text-text-dark mb-2">Confirm password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-cream border-2 border-soft-gray/50 rounded-xl text-text-dark font-body focus:outline-none focus:border-lavender focus:ring-2 focus:ring-lavender/20"
                      placeholder="Confirm your password"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-lavender to-purple-500 hover:from-purple-500 hover:to-lavender text-white font-display text-lg rounded-xl transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
